import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { QueryKeys, TranslationNS } from '@gokrew/models/constants';
import type { CreateHandoverPayload } from '@gokrew/models/handovers';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/context/PermissionContext';
import { permissionKeys } from '@/utility';
import {
  createHandover,
  getDocumentsExpiring,
  getHandoverExceptions,
  getHandoverPreview,
  getProbationEnding,
  getTerminationPreview,
} from '@/api/handovers';
import { invalidateReporting } from '@/hooks/reporting/useReporting';
import { toastIfForbidden } from '@/utils/performance';
import { countOpenExceptions } from './openExceptionsCount';

/**
 * What a leaver hands over, read fresh every time the termination dialog
 * opens: reports, pending approvals and work change by the minute.
 */
export const useTerminationHandoverPreview = (
  employeeId: string | undefined,
  enabled = true
) => {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: [QueryKeys.TerminationPreview, companyId, employeeId],
    queryFn: () => getTerminationPreview(companyId!, employeeId!),
    enabled: enabled && !!companyId && !!employeeId,
    select: (res) => res.data?.handover ?? null,
    staleTime: 0,
  });
};

/**
 * Whether the terminate dialog must ask for the ROE code and recall: only
 * for companies that saved payroll settings (payroll v1). Same query as the
 * handover preview, so no second request.
 */
export const useTerminationRoeRequired = (
  employeeId: string | undefined,
  enabled = true
) => {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: [QueryKeys.TerminationPreview, companyId, employeeId],
    queryFn: () => getTerminationPreview(companyId!, employeeId!),
    enabled: enabled && !!companyId && !!employeeId,
    select: (res) => !!res.data?.roeRequired,
    staleTime: 0,
  });
};

/**
 * What a person hands over without leaving ("Hand over their team"), read
 * fresh every time the dialog opens.
 */
export const useHandoverPreview = (
  employeeId: string | undefined,
  enabled = true
) => {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: [QueryKeys.HandoverPreview, companyId, employeeId],
    queryFn: () => getHandoverPreview(companyId!, employeeId!),
    enabled: enabled && !!companyId && !!employeeId,
    select: (res) => res.data ?? null,
    staleTime: 0,
  });
};

/** Plans a reassignment handover; effective now, it runs straight away. */
export const useCreateHandover = () => {
  const { companyId } = useAuth();
  const qc = useQueryClient();
  const { t } = useTranslation(TranslationNS.Employee);
  return useMutation({
    mutationFn: (payload: CreateHandoverPayload) =>
      createHandover(companyId!, payload),
    onSuccess: (_res, payload) => {
      invalidateReporting(qc, companyId!);
      qc.invalidateQueries({
        queryKey: [QueryKeys.HandoverPreview, companyId],
      });
      toast.success(
        t(
          payload.effectiveAt
            ? 'handover.team.toast.scheduled'
            : 'handover.team.toast.done'
        )
      );
    },
    onError: (error) =>
      toastIfForbidden(error, t('reporting_line.toast.forbidden')),
  });
};

/** HR exception view: what routing and handovers could not settle. */
export const useHandoverExceptions = (enabled = true) => {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: [QueryKeys.HandoverExceptions, companyId],
    queryFn: () => getHandoverExceptions(companyId!),
    enabled: enabled && !!companyId,
    select: (res) => res.data,
  });
};

/** Probation ending soon; only fetched for admins who can read employees. */
export const useProbationEnding = (enabled: boolean) => {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: [QueryKeys.ExceptionsProbationEnding, companyId],
    queryFn: () => getProbationEnding(companyId!),
    enabled: enabled && !!companyId,
    select: (res) => res.data,
  });
};

/** Documents expiring soon; only fetched for admins who can read documents. */
export const useDocumentsExpiring = (enabled: boolean) => {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: [QueryKeys.ExceptionsDocumentsExpiring, companyId],
    queryFn: () => getDocumentsExpiring(companyId!),
    enabled: enabled && !!companyId,
    select: (res) => res.data,
  });
};

/**
 * Open items on the Exceptions page, for the Organization card chip. Reads
 * the same queries as the page, with the same read scopes gating the
 * probation and document cards, so both always show the same number.
 * `isLoaded` stays false until every query the admin can read has answered.
 */
export const useOpenExceptionsCount = () => {
  const { permissions } = usePermissions();
  const canReadEmployees = permissions.has(permissionKeys.employee.read);
  const canReadDocuments = permissions.has(
    permissionKeys.employee_document.read
  );
  const exceptions = useHandoverExceptions();
  const probation = useProbationEnding(canReadEmployees);
  const documents = useDocumentsExpiring(canReadDocuments);

  const isLoaded =
    exceptions.isSuccess &&
    (!canReadEmployees || probation.isSuccess) &&
    (!canReadDocuments || documents.isSuccess);

  return {
    isLoaded,
    count: countOpenExceptions(
      exceptions.data?.counts,
      canReadEmployees ? probation.data : undefined,
      canReadDocuments ? documents.data : undefined
    ),
  };
};
